'use client';

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fmt, type Labels } from './ui';
import { applyCartResponse, beginCartAdd } from '@/lib/cart-cache';

type State = 'idle' | 'adding' | 'added' | 'error';

/** One-click "Add to cart" for product tiles on the home page. */
export function TileActions({
  productId,
  variantId,
  labels,
}: {
  productId: string | null;
  variantId: string;
  labels: Labels;
}) {
  const [state, setState] = useState<State>('idle');
  const queryClient = useQueryClient();

  async function add() {
    if (state === 'adding') return;
    setState('adding');
    const addId = beginCartAdd();
    try {
      const res = await fetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ productId, variantId, quantity: 1 }),
      });
      if (res.status === 401) {
        const here = window.location.pathname + window.location.search;
        window.location.href = fmt(labels.loginHref, { return: encodeURIComponent(here) });
        return;
      }
      if (!res.ok) throw new Error(String(res.status));
      await applyCartResponse(queryClient, await res.json().catch(() => null), addId);
      setState('added');
      window.setTimeout(() => setState('idle'), 2200);
    } catch {
      setState('error');
      window.setTimeout(() => setState('idle'), 2200);
    }
  }


  return (
    <button
      type="button"
      onClick={add}
      disabled={state === 'adding'}
      aria-live="polite"
      className={`h-9 w-full rounded-full px-3 text-[13px] font-semibold transition-colors ${
        state === 'added'
          ? 'bg-hv-ink text-white'
          : state === 'error'
            ? 'bg-hv-red text-white'
            : 'bg-hv-yellow text-hv-ink hover:bg-hv-ink hover:text-white'
      } disabled:opacity-70`}
    >
      {state === 'adding' ? labels.adding : state === 'added' ? labels.added : state === 'error' ? labels.addFailed : labels.addToCart}
    </button>
  );
}
